import { useCallback } from 'react';
import { useFormikContext } from 'formik';
import { useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';

import { drillFragmentData } from '@e-socna/data-layer';
import { EMPTY_STRING } from '@e-socna/empty';
import { EMPTY_ID } from '@e-socna/empty-client';
import type { Id } from '@e-socna/data-layer';

import { generateBasicQueryOptions } from '../../../hooks/useGraphQLBasicQuery.hook';
import { ResolvableMissionTypePage_OneResolvableMissionType_QueryDocument } from '../../../pages/ResolvableMissionType/hooks/useFindOneResolvableMissionTypeQuery.hook';
import { ResolvableMissionTypePage_Tabs_ResolvableMissionType_Fragment } from '../../../pages/ResolvableMissionType/Tabs';
import { ResolvableMissionTypePage_GeneralInfo_ResolvableMissionType_Fragment } from '../../../pages/ResolvableMissionType/GeneralInfo';
import { describeErrors } from '../../../utils/back-errors.util';
import { MessageList } from '../../../components/MessageList';

interface CategorizedFormValues {
  readonly typeId?: Id;
  readonly categoryParentId: string;
  readonly categoryLeaf: string;
}

const NO_CATEGORY = {
  categoryParentId: EMPTY_ID,
  categoryLeaf: EMPTY_STRING,
};

// The category is inherited from the selected type once it is loaded: written
// to the form rather than by the type step, which the wizard unmounts as soon
// as the next step is shown.
export const useMissionTypeSelection = () => {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const { setValues } = useFormikContext<CategorizedFormValues>();

  return useCallback(
    async (typeId: Id | undefined) => {
      await setValues((values) => ({
        ...values,
        typeId,
        ...NO_CATEGORY,
      }));

      if (!typeId) {
        return;
      }

      const data = await queryClient
        .query(
          generateBasicQueryOptions({
            document:
              ResolvableMissionTypePage_OneResolvableMissionType_QueryDocument,
            variables: { id: typeId },
          }),
        )
        .catch((error: unknown) => {
          enqueueSnackbar(<MessageList messages={describeErrors(error)} />, {
            variant: 'error',
          });

          return undefined;
        });
      const type = drillFragmentData(
        [
          ResolvableMissionTypePage_Tabs_ResolvableMissionType_Fragment,
          ResolvableMissionTypePage_GeneralInfo_ResolvableMissionType_Fragment,
        ],
        data?.findOneResolvableMissionType,
      );
      const category = type
        ? {
            categoryParentId: type.categoryParentId,
            categoryLeaf: type.categoryLeaf,
          }
        : NO_CATEGORY;

      await setValues((values) =>
        values.typeId === typeId ? { ...values, ...category } : values,
      );
    },
    [enqueueSnackbar, queryClient, setValues],
  );
};
